import type { Course, Lesson } from "@/types";

export type PersonalHubItem =
  | { kind: "continue"; lesson: Lesson }
  | { kind: "dashboard"; course: Course; href: string }
  | { kind: "setup" }
  | { kind: "purchased"; courseCount: number };

export type PersonalHubModel = {
  items: PersonalHubItem[];
  subtitle?: string;
};

type Input = {
  continueLesson?: Lesson | null;
  dashboardCourse?: Course | null;
  dashboardHref?: string | null;
  purchasedCount: number;
  /** Logged-in users who haven't chosen a home course yet. */
  allowDashboardSetup?: boolean;
  /** Dev-only: force all item types visible for layout QA */
  previewAllItems?: boolean;
};

export function derivePersonalHubModel(input: Input): PersonalHubModel {
  const items: PersonalHubItem[] = [];

  if (input.continueLesson) {
    items.push({ kind: "continue", lesson: input.continueLesson });
  }

  if (input.dashboardCourse && input.dashboardHref) {
    items.push({ kind: "dashboard", course: input.dashboardCourse, href: input.dashboardHref });
  }

  // "Set your dashboard" for users who haven't chosen a home course yet.
  if (input.allowDashboardSetup && (!input.dashboardCourse || input.previewAllItems)) {
    items.push({ kind: "setup" });
  }

  const purchasedCount = input.previewAllItems ? Math.max(input.purchasedCount, 3) : input.purchasedCount;
  if (purchasedCount > 0) {
    items.push({ kind: "purchased", courseCount: purchasedCount });
  }

  // Guests with no continue/purchased: return empty list → hub section hidden.
  const resolved =
    items.length > 0
      ? items
      : input.allowDashboardSetup
        ? [{ kind: "setup" as const }]
        : [];

  return {
    items: resolved,
    subtitle: resolved[0] ? subtitleFor(resolved[0]) : undefined,
  };
}

function subtitleFor(item: PersonalHubItem): string {
  switch (item.kind) {
    case "continue":  return "Pick up where you left off";
    case "dashboard": return "Your home course";
    case "setup":     return "Choose your instrument to get started";
    case "purchased": return "Courses you own with lifetime access";
  }
}
